import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import moment from 'moment';

import { UpdateOccupantDetails } from 'src/api/bookingService';
import { checkSession } from 'src/auth/auth0';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { METADATA_ATTR_URL } from 'src/auth/constants';
import { ErrorCode } from 'src/shared/enum/ErrorCode';

import { mapMonthName } from '../map-month-name-to-number';

export const useUpdateDualOccupantDetails = () =>
    useMutation<void, AxiosError, string>({
        mutationFn: async (vitaRef: string) => {
            const userSession = await checkSession();
            if (!userSession) throw new Error(ErrorMessage.AUTH0_ID_ERROR);

            const { idTokenPayload } = userSession;
            if (!idTokenPayload.sub) return;

            const userData = userSession.idTokenPayload[METADATA_ATTR_URL];

            if (!userData.dateOfBirth.month) throw new Error(ErrorCode.MONTH_NOT_FOUND);

            if (
                !userData.dateOfBirth.year ||
                !userData.dateOfBirth.month ||
                !userData.dateOfBirth.day
            )
                throw new Error(ErrorCode.DATE_OF_BIRTH_NOT_FOUND);

            const dob = moment()
                .year(Number(userData.dateOfBirth.year))
                .month(mapMonthName(userData.dateOfBirth.month))
                .day(userData.dateOfBirth.day)
                .toISOString();

            const requestData = {
                id: idTokenPayload.sub.split('|')[1],
                first_name: userData.firstName,
                last_name: userData.lastName,
                address1: userData.address1,
                address2: userData.address2,
                city: userData.city,
                confirmed_student: true,
                country: userData.country,
                nationality: userData.nationality,
                number: userData.phone.callingCode ?? '',
                phone_code: userData.phone.countryCode ?? '',
                post_code: userData.postCode,
                study_year: userData.studyYear,
                university: userData.university,
                dob,
                email: userData.email,
                preferred_language: userData.preferredLanguage,
                is_spanish_resident: userData.isSpanishResident === 'yes',
                nie_number: userData.nieNumber ?? null,
                nif_number: userData.nifNumber ?? null,
                passport_number: userData.passportNumber ?? null,
                passport_issuing_country: userData.passportIssuingCountry ?? null,
            };

            await UpdateOccupantDetails({
                vitaRef,
                occupant: requestData,
            });
        },
    });
